import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import { MarketConfigForm } from './market-config-form.component';
import { FormProvider, useForm } from 'react-hook-form';
import { TradingFormState } from './trading-form.types';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { PositionManagementConfigForm } from './position-management-config-form.component';
import { TradingScheduleForm } from './trading-schedule-form';
import { Section } from '../../foundation/layout/section';
import { sectionPanelStyle } from '../../foundation/layout/styling';
import { FormConfigButtons } from './form-config-buttons';
import { Loader } from '../../foundation/shared/components/loader';
import {
  defaultBacktestingDataState,
  defaultLiveDataState
} from '../chart/constants/tradng-form-defaults';
import { trpc } from '@client-trpc';
import { TimeframesForm } from './timeframes/timeframes-form.component';
import { TradingMode } from '@shared/types';
import type {
  DefaultTemplatePolicy,
  FormInitSource
} from '@server/trading-layouts/schemas';
import { AdditionalTradingFormContextProvider } from './trading-form-context';
import {
  UiStatePath,
  useTradingFormUIState
} from './trading-form-ui-state.hook';
import { useWidget } from '../widget.hook';
import {
  AppButton,
  AppIconButton
} from '@client/foundation/design-system/components';
const schema = z.object({
  // tradingMode: z.string(),
  // symbol: z.string(),
});

export type TradingFormProps = {
  chartId?: string;
  state?: TradingFormState;
  linkedTradingLayoutId?: string;
  linkedTradingLayoutName?: string;
  // Need to explicitly use mode, which the form is opened with,
  // to override the one applied from the template.
  // This approach was chosen instead of skipping the mode when saving template
  // to avoid type issues
  mode: TradingMode;
  initSource?: FormInitSource;
  defaultTemplatePolicy?: DefaultTemplatePolicy;
  onSubmit: (state: TradingFormState) => void;
  onFormChange?: (state: TradingFormState) => void;
  onFormDestroy?: () => void;
};

const EMPTY_FORM_STATE: TradingFormState = {
  marketConfig: undefined,
  positionManagementConfig: undefined,
  tradingScheduleConfig: undefined,
  timeframes: []
};

export const TradingForm: FC<TradingFormProps> = ({
  onSubmit,
  chartId,
  state,
  linkedTradingLayoutId,
  linkedTradingLayoutName,
  mode,
  initSource,
  defaultTemplatePolicy,
  onFormChange,
  onFormDestroy
}) => {
  const effectiveInitSource: FormInitSource =
    initSource ?? (state ? 'LAYOUT_RESTORE' : 'NEW_FORM');
  const effectiveDefaultTemplatePolicy: DefaultTemplatePolicy =
    defaultTemplatePolicy ??
    (effectiveInitSource === 'NEW_FORM'
      ? 'AUTO_APPLY_DEFAULT_TEMPLATES'
      : 'SKIP_AUTO_APPLY_DEFAULT_TEMPLATES');

  const initialFormState = useMemo<TradingFormState>(() => {
    const baseState =
      state ||
      (mode === 'LIVE' ? defaultLiveDataState : defaultBacktestingDataState);

    return {
      ...baseState,
      marketConfig: baseState.marketConfig
        ? {
            ...baseState.marketConfig,
            // Form mode comes from the window context (LIVE/BACKTESTING workspace).
            // Stored layout/template payload must not switch this mode on restore/apply.
            tradingMode: mode
          }
        : undefined
    };
  }, [mode, state]);

  // RHF reads defaultValues only once; this form can stay mounted between payload changes.
  const formApi = useForm<TradingFormState>({
    defaultValues: initialFormState,
    resolver: zodResolver(schema)
  });

  const { handleSubmit, reset, watch } = formApi;
  const { openWidget } = useWidget();

  const {
    uiState,
    setUiState,
    getUiState,
    isLoaded: isUiStateLoaded
  } = useTradingFormUIState();
  // Local link state lets us enable "Edit layout" immediately after Save,
  // without reopening/remounting TradingForm.
  const [activeLinkedTradingLayoutId, setActiveLinkedTradingLayoutId] =
    useState(linkedTradingLayoutId);
  const [activeLinkedTradingLayoutName, setActiveLinkedTradingLayoutName] =
    useState(linkedTradingLayoutName);

  const { data: defaultTemplates } =
    trpc.tradingTemplates.defaultTemplates.useQuery();

  useEffect(() => {
    // Sync RHF state when incoming layout/state changes without remounting the form.
    reset(initialFormState);
  }, [initialFormState, reset]);

  useEffect(() => {
    // Keep local link state in sync when form payload is replaced externally
    // (e.g. apply from layouts grid or default layout restore).
    setActiveLinkedTradingLayoutId(linkedTradingLayoutId);
    setActiveLinkedTradingLayoutName(linkedTradingLayoutName);
  }, [linkedTradingLayoutId, linkedTradingLayoutName]);

  const handleSaveLayout = useCallback(() => {
    openWidget({
      type: 'SaveTradingLayoutForm',
      formState: watch(),
      onSaved: ({ layoutId, layoutName }) => {
        // Newly created layout should become editable right away.
        setActiveLinkedTradingLayoutId(layoutId);
        setActiveLinkedTradingLayoutName(layoutName);
      }
    });
  }, [openWidget, watch]);

  const handleManageLayouts = useCallback(() => {
    openWidget({
      type: 'ManageTradingLayouts',
      chartId,
      mode,
      activeLayoutId: activeLinkedTradingLayoutId
    });
  }, [activeLinkedTradingLayoutId, chartId, mode, openWidget]);

  const handleEditLayout = useCallback(() => {
    if (!activeLinkedTradingLayoutId) {
      return;
    }

    openWidget({
      type: 'SaveTradingLayoutForm',
      layoutId: activeLinkedTradingLayoutId,
      layoutName: activeLinkedTradingLayoutName,
      formState: watch()
    });
  }, [
    activeLinkedTradingLayoutId,
    activeLinkedTradingLayoutName,
    openWidget,
    watch
  ]);

  useEffect(() => {
    const subscription = watch((values) => {
      if (onFormChange) {
        onFormChange(values as TradingFormState);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, onFormChange]);

  useEffect(() => {
    return () => {
      // Clear large nested values from RHF internals before unmount.
      reset(EMPTY_FORM_STATE);
      if (onFormDestroy) {
        onFormDestroy();
      }
    };
  }, [onFormDestroy, reset]);

  if (!defaultTemplates || !isUiStateLoaded) {
    return <Loader />;
  }

  return (
    <FormProvider {...formApi}>
      <AdditionalTradingFormContextProvider
        value={{
          mode,
          chartId,
          initSource: effectiveInitSource,
          defaultTemplatePolicy: effectiveDefaultTemplatePolicy,
          setUiState,
          getUiState
        }}
      >
        {/* <form onSubmit={handleSubmit(onSubmit)}> */}
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 0.75,
              mb: 1
            }}
          >
            <AppIconButton
              title="Save layout"
              tone="primary"
              onClick={handleSaveLayout}
            >
              <SaveOutlinedIcon />
            </AppIconButton>
            <AppIconButton
              title="Edit linked layout"
              tone="primary"
              onClick={handleEditLayout}
              disabled={!activeLinkedTradingLayoutId}
            >
              <EditOutlinedIcon />
            </AppIconButton>
            <AppIconButton
              title="Manage layouts"
              tone="primary"
              onClick={handleManageLayouts}
            >
              <DashboardCustomizeOutlinedIcon />
            </AppIconButton>
          </Box>
          <Section
            sx={sectionPanelStyle}
            title="Market"
            actionButtons={<FormConfigButtons templateType="market" />}
            collapsible={true}
            expanded={uiState.sections.market.expanded}
            onToggle={(expanded) => {
              const path: UiStatePath = ['sections', 'market'];
              const current = getUiState<Record<string, unknown>>(path, {});
              setUiState(path, { ...current, expanded });
            }}
          >
            <MarketConfigForm mode={mode} />
          </Section>
          <Section
            sx={sectionPanelStyle}
            title="Timeframes"
            collapsible={true}
            expanded={uiState.sections.timeframes.expanded}
            onToggle={(expanded) => {
              const path: UiStatePath = ['sections', 'timeframes'];
              const current = getUiState<Record<string, unknown>>(path, {});
              setUiState(path, { ...current, expanded });
            }}
          >
            <TimeframesForm />
          </Section>
          <Section
            sx={sectionPanelStyle}
            title="Position Management"
            actionButtons={
              <FormConfigButtons templateType="positionManagement" />
            }
            collapsible={true}
            expanded={uiState.sections.positionManagement.expanded}
            onToggle={(expanded) => {
              const path: UiStatePath = ['sections', 'positionManagement'];
              const current = getUiState<Record<string, unknown>>(path, {});
              setUiState(path, { ...current, expanded });
            }}
          >
            <PositionManagementConfigForm />
          </Section>
          <Section
            sx={sectionPanelStyle}
            title="Trading Schedule"
            collapsible={true}
            expanded={uiState.sections.tradingSchedule.expanded}
            onToggle={(expanded) => {
              const path: UiStatePath = ['sections', 'tradingSchedule'];
              const current = getUiState<Record<string, unknown>>(path, {});
              setUiState(path, { ...current, expanded });
            }}
          >
            <TradingScheduleForm />
          </Section>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <AppButton
              tone="secondary"
              size="large"
              sx={{ mr: 2 }}
              onClick={() => reset()}
            >
              Reset
            </AppButton>

            {/* <SubmitButton /> */}
            <AppButton
              tone="primary"
              type="submit"
              size="large"
              onClick={handleSubmit(() => onSubmit(watch()))}
            >
              Submit
            </AppButton>
          </Box>
        </Box>
        {/* </form> */}
      </AdditionalTradingFormContextProvider>
    </FormProvider>
  );
};
